/**
 * Client-side image downscaling for the student profile photo.
 *
 * A picked/taken photo can be several MB; we crop it to a centered square and
 * downscale to a small target (default 256x256) JPEG `data:` URL before upload,
 * keeping the stored payload well under the backend's PROFILE_IMAGE_MAX_BYTES.
 *
 * The geometry (centered-square source crop) is a pure function so it can be
 * unit-tested without a DOM. The actual canvas raster step is a thin wrapper.
 */

import { PROFILE_IMAGE_FULL_MAX_BYTES } from "@diy-avatar/shared/api";

/** Target edge length of the square avatar, in px. */
export const DEFAULT_AVATAR_SIZE = 256;

/** JPEG quality for the encoded avatar. */
export const DEFAULT_AVATAR_QUALITY = 0.82;

export interface SourceDims {
  width: number;
  height: number;
}

/** A centered square sub-rectangle of the source to draw from. */
export interface CropRect {
  sx: number;
  sy: number;
  size: number;
}

/** A source sub-rectangle (arbitrary aspect) to draw from. */
export interface SubRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

/**
 * Pure: compute the largest centered square crop of a `width`x`height` source.
 * Returns the source x/y offset and the square edge length. Clamps to 0 for
 * degenerate (zero/negative) inputs so callers never produce a NaN canvas.
 */
export function centeredSquareCrop(dims: SourceDims): CropRect {
  const w = Math.max(0, Math.floor(dims.width));
  const h = Math.max(0, Math.floor(dims.height));
  const size = Math.min(w, h);
  const sx = Math.floor((w - size) / 2);
  const sy = Math.floor((h - size) / 2);
  return { sx, sy, size };
}

/**
 * Pure: compute the largest CENTERED sub-rectangle of a `width`x`height` source
 * whose aspect ratio (sw/sh) equals `targetAspect`. If the source is wider than
 * the target it crops the sides; if taller it crops top/bottom. Clamps to 0 for
 * degenerate (zero/negative) inputs so callers never produce a NaN canvas.
 */
export function coverCrop(dims: SourceDims, targetAspect: number): SubRect {
  const w = Math.max(0, Math.floor(dims.width));
  const h = Math.max(0, Math.floor(dims.height));
  if (w === 0 || h === 0 || !(targetAspect > 0)) {
    return { sx: 0, sy: 0, sw: 0, sh: 0 };
  }
  const srcAspect = w / h;
  let sw: number;
  let sh: number;
  if (srcAspect > targetAspect) {
    // Source is wider than target: keep full height, crop sides.
    sh = h;
    sw = Math.round(h * targetAspect);
  } else {
    // Source is taller (or equal): keep full width, crop top/bottom.
    sw = w;
    sh = Math.round(w / targetAspect);
  }
  const sx = Math.floor((w - sw) / 2);
  const sy = Math.floor((h - sh) / 2);
  return { sx, sy, sw, sh };
}

/**
 * Minimal drawing surface — the parts of CanvasRenderingContext2D and the
 * source element we use. Declared structurally so tests can pass fakes without
 * a real DOM.
 */
export interface DrawTarget {
  drawImage: (
    source: unknown,
    sx: number,
    sy: number,
    sWidth: number,
    sHeight: number,
    dx: number,
    dy: number,
    dWidth: number,
    dHeight: number,
  ) => void;
}

/**
 * Pure-ish: given a source's natural dims and a draw target, issue the single
 * `drawImage` call that crops to a centered square and scales it into a
 * `size`x`size` destination. Returns the crop used (handy for assertions).
 */
export function drawSquare(
  ctx: DrawTarget,
  source: unknown,
  dims: SourceDims,
  size: number = DEFAULT_AVATAR_SIZE,
): CropRect {
  const crop = centeredSquareCrop(dims);
  ctx.drawImage(source, crop.sx, crop.sy, crop.size, crop.size, 0, 0, size, size);
  return crop;
}

/**
 * Load a File/Blob into an HTMLImageElement (decoded). Browser-only.
 */
function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("image_decode_failed"));
    };
    img.src = url;
  });
}

export interface DownscaleOptions {
  size?: number;
  quality?: number;
  /** Output MIME; defaults to image/jpeg (small + universal). */
  type?: string;
}

/**
 * Browser-only: downscale a picked image File/Blob to a centered-square JPEG
 * `data:` URL. Throws when decoding or canvas encoding is unavailable.
 */
export async function downscaleToDataUrl(
  blob: Blob,
  opts: DownscaleOptions = {},
): Promise<string> {
  const size = opts.size ?? DEFAULT_AVATAR_SIZE;
  const quality = opts.quality ?? DEFAULT_AVATAR_QUALITY;
  const type = opts.type ?? "image/jpeg";

  const img = await loadImage(blob);
  return encodeSquare(img, size, type, quality);
}

/** Source natural dims of a decoded image. */
function imageDims(img: HTMLImageElement): SourceDims {
  return {
    width: img.naturalWidth || img.width,
    height: img.naturalHeight || img.height,
  };
}

/**
 * Browser-only: draw `img` as a centered-square `size`x`size` canvas and encode
 * to a JPEG (or `type`) data URL. Shared by downscaleToDataUrl + the thumb.
 */
function encodeSquare(
  img: HTMLImageElement,
  size: number,
  type: string,
  quality: number,
): string {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas_unsupported");
  drawSquare(ctx as unknown as DrawTarget, img, imageDims(img), size);
  return canvas.toDataURL(type, quality);
}

/** The two renditions produced from one source photo. */
export interface ProfileRenditions {
  /** 256x256 centered-square JPEG data URL (admin/avatars). */
  thumb: string;
  /** Cover-cropped 16:9 JPEG data URL up to 1920x1080 (kiosk placeholder). */
  full: string;
}

/** Options for {@link renderProfileRenditions}. */
export interface ProfileRenditionsOptions {
  /** Cap for the `full` data-URL string length. Default PROFILE_IMAGE_FULL_MAX_BYTES. */
  fullMaxBytes?: number;
}

/** Target aspect (16:9) and bounds for the high-res full rendition. */
const FULL_ASPECT = 16 / 9;
const FULL_MAX_W = 1920;
/** Adaptive JPEG qualities tried in order until the encoded full fits the cap. */
const FULL_QUALITY_LADDER = [0.85, 0.8, 0.72, 0.65, 0.55];

/**
 * Browser-only: decode the source ONCE and produce both profile renditions:
 *   - thumb: the existing 256x256 centered-square JPEG.
 *   - full: cover-cropped to 16:9 then scaled (never upscaled) to fit within
 *     1920x1080, JPEG-encoded with adaptive quality so the data-URL string
 *     length stays <= `fullMaxBytes` (data URLs are ascii base64, so string
 *     length ≈ byte length). If no quality fits, the lowest-quality result is
 *     returned anyway.
 */
export async function renderProfileRenditions(
  blob: Blob,
  opts: ProfileRenditionsOptions = {},
): Promise<ProfileRenditions> {
  const fullMaxBytes = opts.fullMaxBytes ?? PROFILE_IMAGE_FULL_MAX_BYTES;
  const img = await loadImage(blob);

  // thumb — reuse the established square downscale path.
  const thumb = encodeSquare(
    img,
    DEFAULT_AVATAR_SIZE,
    "image/jpeg",
    DEFAULT_AVATAR_QUALITY,
  );

  // full — cover-crop to 16:9, scale to fit 1920x1080 without upscaling.
  const crop = coverCrop(imageDims(img), FULL_ASPECT);
  const outW = Math.min(crop.sw, FULL_MAX_W);
  const outH = Math.round(outW * (9 / 16));
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas_unsupported");
  ctx.drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, outW, outH);

  let full = canvas.toDataURL("image/jpeg", FULL_QUALITY_LADDER[0]);
  for (const q of FULL_QUALITY_LADDER) {
    full = canvas.toDataURL("image/jpeg", q);
    if (full.length <= fullMaxBytes) break;
  }

  return { thumb, full };
}
